import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnInit,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertModalType, ModalService } from '@domgen/dgx-fe-components/modal';
import { GATag } from '@domgen/dgx-fe-ga';

import {
  ManageMyRepairResponse,
  OrbitFlowData,
  RepairHistoryResponse,
} from '../../types/manage-my-repair-types';
import { ManageMyRepairService } from '../../services/manage-my-repair.service';
import {
  AnalyticsService,
  AppConfig,
  Claims2_0WhitelistService,
  FeatureFlagService,
} from '@domgen/dgx-components';
import { ClaimType } from '../../types/plan-detail.types';
import { User } from '../../types/user.model';
import { TranslateService } from '@ngx-translate/core';
import {
  BehaviorSubject,
  Observable,
  filter,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { forbiddenSchemes } from '../../manage-my-repair/beko-schemes-list';
import { CmsService } from '../../services/cms.service';
import { SmartFixBanner } from '../../manage-my-repair/components/smart-fix-banner/smart-fix-banner';
import { ClaimCancellationReasonsComponent } from './modals/claim-cancellation-reasons.component';
import { CONFIG } from '@domgen/dgx-fe-config';
import posthog from 'posthog-js';

export type MMRClaimEvent = {
  claimId: string;
  claimType: ClaimType;
  beforeBookingStage?: boolean;
  orbitFlowData?: OrbitFlowData;
};

export type ClaimsData = {
  loading: boolean;
  data: ManageMyRepairResponse;
  lastUpdated: Date | undefined;
};

type HistoryData = {
  loading: boolean;
  data: RepairHistoryResponse;
};

type CancelModalResultWithReasons = {
  action: 'keep' | 'cancel';
  reason?: string;
};

@Component({
  selector: 'claims-manage-my-repair-container',
  templateUrl: './manage-my-repair.container.html',
  styleUrls: ['./manage-my-repair.container.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManageMyRepairContainerComponent implements OnInit {
  isCancellingFromClaims2 = false;
  planNumber: string | undefined = this.route.snapshot.params.planNumber;

  isHistoryShow = false;

  claims$: Observable<ClaimsData> = this.manageMyRepairService
    .getPlanClaims(this.planNumber)
    .pipe(
      tap((response) => {
        this.staticClaim = response.data;
        this.trackPageLoad(response);
      })
    );

  history$: Observable<HistoryData | null> = this.claims$.pipe(
    filter((response) => response != null && response.data != null),
    switchMap((claimsResponse: ClaimsData) => {
      this.isHistoryShow = this.getIsHistoryShow(claimsResponse);
      const openRepair = !!claimsResponse?.data?.openRepair;

      return this.isHistoryShow
        ? this.manageMyRepairService.getPlanHistory(this.planNumber, openRepair)
        : of(null);
    })
  );

  pageLoaded = false;

  buttonClicked = new BehaviorSubject(false);

  staticClaim: ManageMyRepairResponse;

  user: User;

  isWhitelistUser = false;

  banner$: Observable<SmartFixBanner>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private title: Title,
    private manageMyRepairService: ManageMyRepairService,
    private analyticsService: AnalyticsService,
    private readonly modalService: ModalService,
    private translateService: TranslateService,
    private cms: CmsService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly claimsWhitelistService: Claims2_0WhitelistService,
    @Inject(CONFIG) private readonly config: AppConfig
  ) {
    this.title.setTitle('Manage my repairs | Domestic & General');
  }

  ngOnInit(): void {
    if (!this.planNumber) {
      this.router.navigate(['/', 'my-plans']);
    }
    this.user = this.manageMyRepairService.userData;
    if (this.user?.email) {
      this.isWhitelistUser = this.claimsWhitelistService.checkValidUser(
        this.user.email
      );
    }
    if (this.config.magnoliaApi) {
      this.banner$ = this.cms.getSmartFixBannerConfig();
    }

    const cancelRepairParam = this.route.snapshot.queryParams['cancelRepair'];
    if (cancelRepairParam === 'true') {
      this.claims$
        .pipe(
          filter((claimsData) => !!claimsData?.data?.openRepair),
          take(1),
          tap((claimsData) => {
            this.isCancellingFromClaims2 = true;
            const openClaim = claimsData.data.openRepair;
            if (openClaim) {
              const claimEvent: MMRClaimEvent = {
                claimId: openClaim.id,
                claimType: 'REPAIR',
              };
              this.dispatchCancelClaim(this.planNumber, claimEvent);
            }
          })
        )
        .subscribe();
    }

    // LiveChat.hideLiveChatLauncher();
  }

  get isNewOrbitFlow(): boolean {
    if (!this.staticClaim) {
      return false;
    }

    const isClaims2Enabled = this.featureFlagService.isFeatureFlagEnabled(
      'CLAIMS_2_0',
      'CLAIMS_2_0'
    );

    return isClaims2Enabled && this.isWhitelistUser;
  }

  get isOrbitPaymentFlowEnabled(): boolean {
    return this.featureFlagService.isFeatureFlagEnabled(
      'claims-2-0-orbit-payment'
    );
  }

  backButtonClicked(): void {
    window.location.href = this.config.plansHomePage;
  }

  viewPlanDetails(): void {
    if (!this.planNumber) {
      console.error('No plan number');
      return;
    }
    window.location.href = `${this.config.plansHomePage}/${this.planNumber}`;
  }

  navigateToClaims2Flow(data): void {
    const initialQueryParam = {};
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        if (data[key] !== undefined) {
          initialQueryParam[key] = data[key];
        }
      }
    }

    const encodedSession = encodeURIComponent(
      JSON.stringify(initialQueryParam)
    );

    const sessionId = posthog['brightClaims'].get_session_id();
    const distinctId = posthog['brightClaims'].get_distinct_id();

    window.location.href = `${this.config['brightClaimsUrl']}/${this.planNumber}/orbit?new_session=${encodedSession}#sessionId=${sessionId}&distinctId=${distinctId}`;
  }

  navigateToClaims2Rebooking(claimId: string) {
    window.location.href = `${this.config['brightClaimsUrl']}/my-plans/${this.planNumber}/claims/${claimId}/rebook`;
  }

  createClaim({
    claimType,
    beforeBookingStage,
    orbitFlowData,
  }: MMRClaimEvent): void {
    const trackText: `Book a ${string}` | `Continue ${string} booking` =
      beforeBookingStage
        ? `Continue ${claimType.toLowerCase()} booking`
        : `Book a ${claimType.toLowerCase()}`;

    this.trackCTAClickEvent(trackText);
    this.buttonClicked.next(true);

    if (orbitFlowData) {
      const selectedProductType =
        orbitFlowData.selectedProductType ?? undefined;

      this.navigateToClaims2Flow({
        ...orbitFlowData,
        selectedProductType,
      });
      return;
    }
    this.manageMyRepairService
      .createClaim(
        this.planNumber,
        claimType,
        null,
        !beforeBookingStage ? this.staticClaim.item : null
      )
      .subscribe({
        next: () => this.buttonClicked.next(false),
        error: () => {
          this.buttonClicked.next(false);
        },
      });
  }

  generateCancellationReasons(): string[] {
    return [
      this.translateService.instant(
        'MMR.CANCEL_CLAIM.REPAIR.CANCELLATION_REASONS.1'
      ),
      this.translateService.instant(
        'MMR.CANCEL_CLAIM.REPAIR.CANCELLATION_REASONS.2'
      ),
      this.translateService.instant(
        'MMR.CANCEL_CLAIM.REPAIR.CANCELLATION_REASONS.3'
      ),
      this.translateService.instant(
        'MMR.CANCEL_CLAIM.REPAIR.CANCELLATION_REASONS.4'
      ),
      this.translateService.instant(
        'MMR.CANCEL_CLAIM.REPAIR.CANCELLATION_REASONS.5'
      ),
    ];
  }

  cancelClaim(mmrClaimEvent: MMRClaimEvent): void {
    const { claimType } = mmrClaimEvent;
    if (claimType !== 'SERVICE') {
      this.cancelClaimWithReasons(mmrClaimEvent);
      return;
    }

    const data = this.staticClaim.openService;

    const showSimpleCancelText =
      mmrClaimEvent.beforeBookingStage || !data.rebookable;

    const titleText = `MMR.CANCEL_CLAIM.${claimType}.TITLE`;
    const bodyText = `MMR.CANCEL_CLAIM.${claimType}.${
      showSimpleCancelText ? 'SIMPLE_CANCEL' : 'FULL_CANCEL'
    }`;

    const isDesktop = window.innerWidth > 768;

    this.trackCTAClickEvent(`Cancel this ${claimType.toLowerCase()}`);

    this.modalService
      .openAlert({
        maxWidth: isDesktop ? '424px' : '312px',
        data: {
          body: this.translateService.instant(bodyText),
          gaData: null,
          primaryCta: {
            gaAdditionalData: this.getCancelModalTracking('asv', 'yes'),
            text: this.translateService.instant('MMR.CANCEL_CLAIM.YES'),
            buttonVariant: 'secondary',
            buttonFill: true,
          },
          secondaryCta: {
            gaAdditionalData: this.getCancelModalTracking('asv', 'no'),
            text: this.translateService.instant('MMR.CANCEL_CLAIM.GO_BACK'),
            buttonVariant: 'primary',
            buttonFill: true,
          },
          disableModalInitGATagging: true,
          title: this.translateService.instant(titleText),
          type: AlertModalType.Plain,
        },
      })
      .afterClosed$.pipe(take(1))
      .subscribe((result) => {
        if (result) {
          this.dispatchCancelClaim(this.planNumber, mmrClaimEvent);
        }
      });
  }

  cancelClaimWithReasons(mmrClaimEvent: MMRClaimEvent): void {
    const data = this.staticClaim.openRepair;

    const nonEngineerClaim = ['Self Send', 'Courier', 'Drop off'].includes(
      data.serviceOptionType
    );

    const hasAmendableBooking =
      data.cancelable && data.rebookable && !nonEngineerClaim;

    const titleText = `MMR.CANCEL_CLAIM.REPAIR.TITLE_REASONS`;
    const subTitleText = `MMR.CANCEL_CLAIM.REPAIR.TITLE_SUBTEXT`;
    const bodyText = `MMR.CANCEL_CLAIM.REPAIR.CANCEL_BODY`;
    const isDesktop = window.innerWidth > 768;

    const reasons = this.generateCancellationReasons();

    this.modalService
      .open(ClaimCancellationReasonsComponent, {
        maxWidth: isDesktop ? '440px' : '312px',
        data: {
          title: this.translateService.instant(titleText),
          body: this.translateService.instant(bodyText),
          subTitle: this.translateService.instant(subTitleText),
          reasons,
          buttons: [
            {
              text: this.translateService.instant(
                'MMR.CANCEL_CLAIM.KEEP_REPAIR'
              ),
              action: 'close',
              variant: 'secondary',
            },
            {
              text: this.translateService.instant(
                'MMR.CANCEL_CLAIM.CANCEL_REPAIR'
              ),
              action: 'submit',
              variant: 'primary',
            },
          ],
          errorMessageOther: this.translateService.instant(
            'MMR.CANCEL_CLAIM.REPAIR.ENTER_REASON'
          ),
          errorMessage: this.translateService.instant(
            'MMR.CANCEL_CLAIM.REPAIR.CHOOSE_REASON'
          ),
          hasAmendableBooking,
        },
      })

      .afterClosed$.pipe(take(1))
      .subscribe((result: CancelModalResultWithReasons) => {
        if (result?.action === 'keep') {
          this.trackCancelRepairWithReasons('keep repair', undefined);
          return;
        } else if (result?.action === 'cancel') {
          this.trackCancelRepairWithReasons('cancel repair', result.reason);
          this.dispatchCancelClaim(this.planNumber, mmrClaimEvent);
        }
      });
  }

  trackCancelRepairWithReasons(
    eventLabel: 'cancel repair' | 'keep repair',
    reason?: string
  ): void {
    const productName = this.getProductName();

    this.analyticsService.push({
      event: 'genericGAEvent',
      clientName: 'DG Claims',
      pagename: 'claims-manage-my-repairs',
      eventCategory: 'current_booking',
      eventAction: 'cancel booking - reason',
      eventLabel: reason ? `${eventLabel} - ${reason}` : eventLabel,
      planNumber: this.planNumber,
      productName: productName,
      companyCode: this.staticClaim.companyCode,
      countryCode: this.staticClaim.countryCode,
      customerID: this.user?.sub || '',
      claimedAccountBrand: this.staticClaim?.openRepair?.bookingOEM,
      claimedModelNumber: this.staticClaim?.item.model_number,
      claimedManufacturer: this.staticClaim?.item.manufacturer,
      claimedClientName: this.staticClaim?.openRepair?.bookingOEM,
    });
  }

  trackCancelRepairSuccess(): void {
    const productName = this.getProductName();

    this.analyticsService.push({
      event: 'genericGAEvent',
      clientName: 'DG Claims',
      pagename: 'claims-manage-my-repairs',
      eventCategory: 'current_booking',
      eventAction: 'modal - cancel booking - success',
      eventLabel: 'repair successfully cancelled',
      planNumber: this.planNumber,
      productName: productName,
      companyCode: this.staticClaim.companyCode,
      countryCode: this.staticClaim.countryCode,
      customerID: this.user?.sub || '',
      claimedAccountBrand: this.staticClaim?.openRepair?.bookingOEM,
      claimedModelNumber: this.staticClaim?.item.model_number,
      claimedManufacturer: this.staticClaim?.item.manufacturer,
      claimedClientName: this.staticClaim?.openRepair?.bookingOEM,
    });
  }

  dispatchCancelClaim(planNumber: string, mmrClaimEvent: MMRClaimEvent): void {
    this.buttonClicked.next(true);
    this.manageMyRepairService
      .cancelClaim(planNumber, mmrClaimEvent.claimId)
      .subscribe({
        next: () => {
          this.buttonClicked.next(false);
          this.showCancelClaimSuccess(mmrClaimEvent);

          this.manageMyRepairService.dispatchGetPlanClaims(planNumber);
          if (this.isCancellingFromClaims2) {
            this.isCancellingFromClaims2 = false;
            // Remove the param from the URL once cancelled
            this.router.navigate([], {
              relativeTo: this.route,
              queryParams: { cancelRepair: null },
              queryParamsHandling: 'merge',
              replaceUrl: true,
            });
          }
        },
        error: (error: HttpErrorResponse) => {
          this.showCancelClaimErrorModal(error);
          if (this.isCancellingFromClaims2) {
            this.isCancellingFromClaims2 = false;
            this.router.navigate([], {
              relativeTo: this.route,
              queryParams: { cancelRepair: null },
              queryParamsHandling: 'merge',
              replaceUrl: true,
            });
          }
        },
      });
  }

  showCancelClaimSuccess(mmrClaimEvent: MMRClaimEvent): void {
    const titleText = `MMR.CANCEL_CLAIM.${mmrClaimEvent.claimType}.SUCCESS.TITLE`;
    const bodyText = `MMR.CANCEL_CLAIM.${mmrClaimEvent.claimType}.SUCCESS.BODY`;
    const isDesktop = window.innerWidth > 768;

    this.trackCancelRepairSuccess();

    this.modalService.openAlert({
      maxWidth: isDesktop ? '424px' : '312px',
      data: {
        title: this.translateService.instant(titleText),
        gaData: null,
        body: this.translateService.instant(bodyText),
        type: AlertModalType.Plain,
        primaryCta: {
          gaAdditionalData: null,
          text: this.translateService.instant('MMR.CANCEL_CLAIM.CLOSE'),
          buttonVariant: 'primary',
          buttonFill: true,
        },
      },
    });
  }

  showCancelClaimErrorModal(error: HttpErrorResponse): void {
    const resolution = window.innerWidth > 768 ? 'DESKTOP' : 'MOBILE';
    const isDesktop = window.innerWidth > 768;

    this.buttonClicked.next(false);
    this.trackCancelError(error);
    this.modalService.openAlert({
      maxWidth: isDesktop ? '424px' : '312px',
      data: {
        title: this.translateService.instant('MMR.CANCEL_CLAIM.ERROR.TITLE'),
        gaData: null,
        body: this.translateService.instant(
          `MMR.CANCEL_CLAIM.ERROR.BODY.${resolution}`
        ),
        type: AlertModalType.Plain,
        primaryCta: {
          gaAdditionalData: null,
          text: this.translateService.instant('MMR.CANCEL_CLAIM.GO_BACK'),
          buttonVariant: 'primary',
          buttonFill: true,
        },
      },
    });
  }

  rebookClaim({ claimId, claimType }: MMRClaimEvent): void {
    this.trackCTAClickEvent('Rebook this visit');
    if (this.isNewOrbitFlow) {
      this.navigateToClaims2Rebooking(claimId);
    } else {
      const claim = [
        this.staticClaim.openRepair,
        this.staticClaim.openService,
      ].find((i) => i?.id === claimId);

      const latestClaimState = claim?.appointmentHistory?.pop();
      const timeSlot: 'AM' | 'PM' | undefined = ['AM', 'PM'].includes(
        latestClaimState.timeSlot
      )
        ? (latestClaimState.timeSlot as 'AM' | 'PM')
        : undefined;
      const crossSell = null;

      this.buttonClicked.next(true);

      this.manageMyRepairService
        .rebookClaim(this.planNumber, claimId, claimType, crossSell, timeSlot)
        .pipe(take(1))
        .subscribe({
          next: () => this.buttonClicked.next(false),
          error: () => {
            this.buttonClicked.next(false);
          },
        });
    }
  }

  trackPageLoad(pageData: ClaimsData): void {
    if (this.pageLoaded || !pageData?.data) return;
    const { item, planState, openRepair, openService } = this.staticClaim;
    this.pageLoaded = true;
    const productName = this.getProductName();

    let trackerValidForProduct = false;

    if (openRepair?.isClaimsTrackerEnabled) {
      trackerValidForProduct = true;
    }

    this.analyticsService.push({
      event: 'spa-pageview',
      eventCategory: 'MMR-page',
      pagename: 'claims-manage-my-repairs',
      category: this.isNewOrbitFlow ? 'claims-2.0' : 'claims-dgx',
      customerID: this.user?.sub || '',
      DigitalID: this.user?.['custom:digital_id'] || '',
      planNumber: this.planNumber,
      productName,
      productStatus: planState,
      productModel: item.model_number,
      openRepair: !!openRepair,
      openService: !!openService,
      repairTimeline:
        trackerValidForProduct && openRepair?.isClaimsTrackerEnabled,
      repairer: openRepair?.repairer?.name || openService?.repairer?.name || '',
    });

    this.trackOpenClaims('Repair', !!openRepair, this.user);
    this.trackOpenClaims('Service', !!openService, this.user);
    this.trackRepairerDetails(this.user, trackerValidForProduct);
  }

  trackRepairerDetails(user: User, trackerValidForProduct: boolean): void {
    const { item, planState, openRepair } = this.staticClaim;

    this.analyticsService.push({
      event: 'genericGAEvent',
      eventCategory: 'MMR-page',
      eventAction: `collect repairer`,
      eventLabel: openRepair?.repairer?.name,
      repairTimeline:
        trackerValidForProduct && openRepair?.isClaimsTrackerEnabled,
      pagename: 'claims-manage-my-repairs',
      category: this.isNewOrbitFlow ? 'claims-2.0' : 'claims-dgx',
      customerID: user?.sub || '',
      DigitalID: user?.['custom:digital_id'] || '',
      planNumber: this.planNumber,
      productName: this.getProductName(),
      productStatus: planState,
      productModel: item.model_number,
    });
  }

  trackOpenClaims(
    type: 'Service' | 'Repair',
    claimOpen: boolean,
    user: User
  ): void {
    const { item, planState } = this.staticClaim;

    this.analyticsService.push({
      event: 'genericGAEvent',
      eventCategory: 'MMR-page',
      eventAction: `open${type}`,
      eventLabel: claimOpen,
      pagename: 'claims-manage-my-repairs',
      category: this.isNewOrbitFlow ? 'claims-2.0' : 'claims-dgx',
      customerID: user?.sub || '',
      DigitalID: user?.['custom:digital_id'] || '',
      planNumber: this.planNumber,
      productName: this.getProductName(),
      productStatus: planState,
      productModel: item.model_number,
    });
  }

  trackCTAClickEvent(
    eventLabel:
      | `Book a ${string}`
      | 'Rebook this visit'
      | `Cancel this ${string}`
      | `Continue ${string} booking`
      | 'Track my repair'
  ): void {
    const productName = this.getProductName();

    posthog['brightClaims'].capture(
      eventLabel.split(' ').join('_').toLowerCase(),
      {}
    );

    this.analyticsService.push({
      event: 'genericGAEvent',
      pagename: 'claims-manage-my-repairs',
      eventCategory: 'MMR-page',
      eventAction: 'interaction',
      eventLabel: eventLabel,
      planNumber: this.planNumber,
      productName: productName,
      companyCode: this.staticClaim.companyCode,
      countryCode: this.staticClaim.countryCode,
    });
  }

  trackCancelError(errorData: HttpErrorResponse): void {
    const { item, planState } = this.staticClaim;
    this.pageLoaded = true;
    const productName = this.getProductName();

    this.analyticsService.push({
      event: 'spa-pageview',
      pagename: 'call-cs-cancel-booking',
      category: this.isNewOrbitFlow ? 'claims-2.0' : 'claims-dgx',
      customerID: this.user?.sub || '',
      DigitalID: this.user?.['custom:digital_id'] || '',
      planNumber: this.planNumber,
      productName,
      productStatus: planState,
      productModel: item.model_number,
    });

    this.analyticsService.push({
      event: 'genericGAEvent',
      pagename: 'call-cs-cancel-booking',
      eventCategory: 'cancel_errors',
      eventAction: errorData.status,
      eventLabel: errorData.error?.message,
      planNumber: this.planNumber,
      productName,
    });
  }

  getProductName(): string {
    const { item } = this.staticClaim;
    const productName = `${item.manufacturer || ''} ${
      item.code_description || ''
    }`.toLowerCase();

    return productName;
  }

  getCancelModalTracking(type: 'asv' | 'booking', action: 'yes' | 'no'): GATag {
    const productName = this.getProductName();
    return {
      event: 'genericGAEvent',
      pagename: 'claims-manage-my-repairs',
      eventAction: 'plan_details',
      eventCategory: `current_${type}`,
      planNumber: this.planNumber,
      productName: productName,
      eventLabel: `cancel_${type}_${action}_clicked`,
    };
  }

  trackClaim(claimId: string): void {
    this.trackCTAClickEvent('Track my repair');

    this.router.navigate([
      '/',
      'my-plans',
      this.planNumber,
      'claims',
      claimId,
      'engineer-tracking',
    ]);
  }

  private checkEligibleBekoScheme(claimsResponse: ClaimsData): boolean {
    const isForbiddenPlanNumber = forbiddenSchemes.some((scheme) =>
      claimsResponse?.data?.planNumber?.startsWith(scheme)
    );
    const isBeko =
      claimsResponse?.data?.item?.manufacturer?.toLowerCase() === 'beko';

    return isBeko && !isForbiddenPlanNumber;
  }

  private getIsHistoryShow(claimsResponse: ClaimsData): boolean {
    const isWhp = claimsResponse?.data?.bookingOEM === 'whirlpool';
    const manufacturer =
      claimsResponse?.data?.item?.manufacturer?.toLowerCase();
    const isVokera = manufacturer === 'vokera';
    const isBeko = manufacturer === 'beko';
    const isEligibleBeko = this.checkEligibleBekoScheme(claimsResponse);
    const claimsTrackerEnabled =
      claimsResponse?.data?.openRepair?.isClaimsTrackerEnabled && !isBeko;

    return isWhp || isVokera || isEligibleBeko || claimsTrackerEnabled;
  }
}
